import React, { useEffect, useState, useCallback } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../../pages/PageHeader";
import Loader from "../../common/Loader";
import DataTable from "react-data-table-component";
import { triggerAlert } from "../../utils";
import { AdminCoupons } from "../../utils/APIClient";
import { confirm } from "../../common/ConfirmDialog";
import CouponDialog from "./CouponDialog";
import CouponRedemptionsDialog from "./CouponRedemptionsDialog";

/**
 * Settings > Coupons
 *
 * Admin CRUD over apps/billing/Coupon rows. Coupons discount only the
 * initial subscribe charge; ARB recurring stays at plan price so month
 * 2 onward auto-bills full. Destroy is a soft-delete on the server
 * (is_active=False) so redemption FKs stay intact for audit.
 */

const CATEGORY_OPTIONS = [
    { value: "avatar", label: "Avatar" },
    { value: "calling_v2", label: "Calling" },
    { value: "workflow", label: "Workflow" },
];

const discountSummary = (row) => {
    if (row.discount_type === "complimentary") return "Complimentary (100% off)";
    if (row.discount_type === "percentage") {
        const cap = row.max_discount_amount != null
            ? ` (up to $${Number(row.max_discount_amount).toFixed(2)})`
            : "";
        return `${Number(row.discount_value || 0)}% off${cap}`;
    }
    if (row.discount_type === "fixed_amount") {
        return `$${Number(row.discount_value || 0).toFixed(2)} off`;
    }
    return row.discount_type || "—";
};

const categoryChips = (row) => {
    const cats = row.applies_to_categories || [];
    if (!cats.length) return "All";
    return cats
        .map((c) => CATEGORY_OPTIONS.find((o) => o.value === c)?.label || c)
        .join(", ");
};

const fmtDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toISOString().slice(0, 10);
    } catch {
        return iso;
    }
};

export default function Coupons() {
    document.title = "Digital Employees - Coupons";
    const mainHeading = "Coupons";
    const heading = "Settings / Coupons";

    const [isLoading, setIsLoading] = useState(true);
    const [listData, setListData] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [redemptionsOpen, setRedemptionsOpen] = useState(false);
    const [redemptionsCoupon, setRedemptionsCoupon] = useState(null);

    const fetchListData = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = {};
            if (categoryFilter) params.category = categoryFilter;
            if (statusFilter) params.is_active = statusFilter;
            if (search) params.search = search;
            const resp = await AdminCoupons.list(params);
            const rows = Array.isArray(resp.data)
                ? resp.data
                : resp.data?.results || [];
            rows.forEach((item, i) => (item.Num = i + 1));
            setListData(rows);
        } catch (e) {
            setListData([]);
            triggerAlert("error", "Oops...", "Failed to load coupons.");
        } finally {
            setIsLoading(false);
        }
    }, [categoryFilter, statusFilter, search]);

    useEffect(() => {
        fetchListData();
    }, [fetchListData]);

    const handleCreate = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const handleEdit = (row) => {
        setEditing(row);
        setDialogOpen(true);
    };

    const handleDelete = async (row) => {
        const ok = await confirm({
            title: "Deactivate coupon?",
            message: `Deactivate coupon "${row.code}"? Redemption history is preserved.`,
            confirmLabel: "Deactivate",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await AdminCoupons.remove(row.id);
            triggerAlert("success", "Success", "Coupon deactivated.");
            fetchListData();
        } catch (e) {
            triggerAlert("error", "Oops...", "Could not deactivate coupon.");
        }
    };

    const handleToggleActive = async (row) => {
        try {
            // PATCH (not PUT) so the serializer only validates the one field
            // we're sending. A PUT here would fail with "code / name /
            // discount_type / valid_from / valid_until are required".
            await AdminCoupons.patch(row.id, { is_active: !row.is_active });
            triggerAlert("success", "Success", "Status updated.");
            fetchListData();
        } catch (e) {
            triggerAlert("error", "Oops...", "Could not update status.");
        }
    };

    const handleViewRedemptions = (row) => {
        setRedemptionsCoupon(row);
        setRedemptionsOpen(true);
    };

    const columns = [
        { name: "#", width: "60px", cell: (r) => r.Num },
        {
            name: "Code",
            sortable: true,
            selector: (r) => r.code,
            cell: (r) => (
                <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{r.code}</span>
            ),
        },
        {
            name: "Name",
            selector: (r) => r.name,
            cell: (r) => r.name,
        },
        {
            name: "Discount",
            cell: (r) => discountSummary(r),
        },
        {
            name: "Categories",
            cell: (r) => categoryChips(r),
        },
        {
            name: "Audience",
            width: "110px",
            cell: (r) => (r.audience === "restricted" ? "Restricted" : "Public"),
        },
        {
            name: "Redeemed",
            width: "120px",
            cell: (r) => {
                const cap = r.max_redemptions_total ?? "∞";
                return `${r.times_redeemed || 0} / ${cap}`;
            },
        },
        {
            name: "Valid",
            cell: (r) => `${fmtDate(r.valid_from)} → ${fmtDate(r.valid_until)}`,
        },
        {
            name: "Status",
            width: "100px",
            cell: (r) =>
                r.is_active ? (
                    <span className="btn btn-success btn-sm activebtn btn-rounded">Active</span>
                ) : (
                    <span className="btn btn-danger btn-sm Inactivebtn btn-rounded">Inactive</span>
                ),
        },
        {
            name: "Actions",
            width: "220px",
            cell: (r) => (
                <div className="d-flex gap-1">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(r)}>Edit</button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleViewRedemptions(r)}>
                        Uses
                    </button>
                    <button
                        className={`btn btn-sm ${r.is_active ? "btn-outline-warning" : "btn-outline-success"}`}
                        onClick={() => handleToggleActive(r)}
                    >
                        {r.is_active ? "Pause" : "Resume"}
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(r)}>Delete</button>
                </div>
            ),
        },
    ];

    const customStyles = {
        table: { style: { backgroundColor: "#ffffff", borderRadius: "8px" } },
        headRow: {
            style: {
                backgroundColor: "#064c77",
                borderBottom: "1px solid #dee2e6",
                minHeight: "52px",
            },
        },
        headCells: {
            style: {
                color: "#ffffff",
                fontSize: "13px",
                fontWeight: "700",
                textTransform: "uppercase",
            },
        },
        rows: {
            style: {
                minHeight: "56px",
                backgroundColor: "#ffffff",
                "&:hover": { backgroundColor: "#f8f9fa", cursor: "pointer" },
            },
        },
        cells: {
            style: {
                fontSize: "14px",
                color: "#495057",
                borderBottom: "1px solid #f1f3f5",
            },
        },
        pagination: { style: { borderTop: "1px solid #dee2e6", padding: "8px" } },
    };

    return (
        <>
            <div id="layout-wrapper">
                <Header />
                <Sidebar />

                <div className="main-content">
                    <div className="page-content" style={{ backgroundColor: "#ededed" }}>
                        <div className="container-fluid">
                            <PageHeader mainHeading={mainHeading} heading={heading} />

                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="row align-items-end mb-3 g-2">
                                                <div className="col-md-3">
                                                    <label className="form-label mb-1 font_weight_500">Filter by category</label>
                                                    <select
                                                        className="form-select"
                                                        value={categoryFilter}
                                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                                    >
                                                        <option value="">All categories</option>
                                                        {CATEGORY_OPTIONS.map((c) => (
                                                            <option key={c.value} value={c.value}>{c.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label mb-1 font_weight_500">Status</label>
                                                    <select
                                                        className="form-select"
                                                        value={statusFilter}
                                                        onChange={(e) => setStatusFilter(e.target.value)}
                                                    >
                                                        <option value="">All</option>
                                                        <option value="true">Active</option>
                                                        <option value="false">Inactive</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="form-label mb-1 font_weight_500">Search</label>
                                                    <input
                                                        className="form-control"
                                                        placeholder="Search by code or name"
                                                        value={search}
                                                        onChange={(e) => setSearch(e.target.value)}
                                                    />
                                                </div>
                                                <div className="col-md-2 text-md-end">
                                                    <button className="btn btn-primary" onClick={handleCreate}>
                                                        + New Coupon
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="alert alert-info py-2 mb-3" role="alert">
                                                <strong>How this works:</strong> Coupons only discount the customer's
                                                first monthly charge. The recurring subscription (month 2 onward)
                                                always bills at the full plan price.
                                            </div>

                                            {!isLoading ? (
                                                <DataTable
                                                    columns={columns}
                                                    data={listData}
                                                    pagination
                                                    fixedHeader
                                                    fixedHeaderScrollHeight="600px"
                                                    persistTableHead
                                                    customStyles={customStyles}
                                                />
                                            ) : (
                                                <div className="loader-overlay text-white">
                                                    <Loader />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>

            {dialogOpen && (
                <CouponDialog
                    coupon={editing}
                    onClose={() => setDialogOpen(false)}
                    onSaved={() => {
                        setDialogOpen(false);
                        fetchListData();
                    }}
                />
            )}
            {redemptionsOpen && redemptionsCoupon && (
                <CouponRedemptionsDialog
                    coupon={redemptionsCoupon}
                    onClose={() => setRedemptionsOpen(false)}
                />
            )}
        </>
    );
}
